// OPD fee schedule — backend/routes/opd-fee-schedule.js, utils/opdFeeSchedule.js.

export type OpdFeeCategory = 'registration' | 'consultation' | 'procedure' | 'other';
export type OpdFeeAppliesTo = 'all' | 'new_patient' | 'returning_patient';

export const CATEGORY_LABELS: Record<OpdFeeCategory, string> = {
  registration: 'Registration',
  consultation: 'Consultation',
  procedure: 'Procedure',
  other: 'Other charges',
};

export const APPLIES_TO_LABELS: Record<OpdFeeAppliesTo, string> = {
  all: 'Every visit',
  new_patient: "Patient's first visit only",
  returning_patient: 'Returning patients only',
};

/** A fee for one branch and/or department, overriding the item's default. */
export interface OpdFeeOverride {
  id: number;
  item_id: number;
  branch_id: number | null;
  specialty_id: number | null;
  branch_name?: string | null;
  specialty_name?: string | null;
  fee: string;
  active: boolean;
}

/** GET /api/opd-fees — an item as the admin screen edits it. Money is NUMERIC-as-string. */
export interface OpdFeeItem {
  id: number;
  code: string;
  name: string;
  category: OpdFeeCategory;
  default_fee: string;
  gst_percent: string;
  hsn_code: string;
  applies_to: OpdFeeAppliesTo;
  auto_add: boolean;
  max_discount_percent: string;
  sort_order: number;
  active: boolean;
  updated_at: string;
  fees: OpdFeeOverride[];
}

export interface OpdFeeItemInput {
  code?: string;
  name?: string;
  category?: OpdFeeCategory;
  default_fee?: number;
  gst_percent?: number;
  hsn_code?: string;
  applies_to?: OpdFeeAppliesTo;
  auto_add?: boolean;
  max_discount_percent?: number;
  sort_order?: number;
  active?: boolean;
}

/** GET /api/opd-fees/quote — one item priced for the bill being raised. */
export interface OpdFeeQuoteItem {
  id: number;
  code: string;
  name: string;
  category: OpdFeeCategory;
  fee: number;
  fee_source: 'branch_department' | 'department' | 'branch' | 'default';
  default_fee: number;
  gst_percent: number;
  hsn_code: string;
  applies_to: OpdFeeAppliesTo;
  auto_add: boolean;
  max_discount_percent: number;
  include_by_default: boolean;
}

export interface OpdFeeQuote {
  items: OpdFeeQuoteItem[];
  context: {
    branch_id: number;
    specialty_id: number | null;
    specialty_name: string | null;
    /** True when the department came from the visit's doctor — the picker is then fixed. */
    specialty_from_visit: boolean;
    doctor_name: string | null;
    is_new_patient: boolean;
  };
  /** Whether this role holds billing:fee-override (may change fees, exceed discount limits, add unscheduled charges). */
  can_override: boolean;
}
